import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { BottomSheet } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet'
import { FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { useQuestionnaireTrackingProps } from '@/modules/questionnaire/hooks/useQuestionnaireTrackingProps'

type QuestionInfoSheetProps = {
  title?: string
  extraInfo: string
  questionLinkId: string
}

const QuestionInfoSheet = ({ title, extraInfo, questionLinkId }: QuestionInfoSheetProps) => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useMediaQuery(breakpoints.toS) || Capacitor.isNativePlatform()
  const trackingProps = useQuestionnaireTrackingProps()

  function open() {
    setIsOpen(true)
    mixpanel.track('questionnaire_info_button_opened', {
      question_link_id: questionLinkId,
      ...trackingProps,
    })
  }

  function close() {
    setIsOpen(false)
  }

  const content = <Markdown content={extraInfo} typography='BodyMediumRegular' />

  return (
    <>
      <StInfoButton type='button' onClick={open} aria-label='Extra informatie'>
        <FontAwesomeIcon icon={['fal', 'info-circle']} fontSize={20} />
      </StInfoButton>

      {isMobile ? (
        <BottomSheet fitContent visible={isOpen} onClose={close}>
          <Padding
            px={theme.UI.Spacing.Space4}
            pb={theme.UI.Spacing.Space6}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            {title ? <H2>{title}</H2> : null}
            {content}
          </Padding>
        </BottomSheet>
      ) : (
        <Modal isVisible={isOpen} onClose={close} maxHeight='90vh'>
          <StModalContent>
            <FlexRow justifyContent='space-between' alignItems='center'>
              {title ? <H2 noMargin>{title}</H2> : <span aria-hidden='true' />}
              <StCloseButton type='button' onClick={close} aria-label='Sluiten'>
                <FontAwesomeIcon icon={['far', 'close']} fontSize={20} color={theme.Colors.Neutrals.Darker} />
              </StCloseButton>
            </FlexRow>
            {content}
          </StModalContent>
        </Modal>
      )}
    </>
  )
}

export { QuestionInfoSheet }

const StInfoButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: ${({ theme }) => theme.UI.SpacingPx.Space2};
  color: ${({ theme }) => theme.Colors.Theme.Primary};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  }

  &:focus-visible {
    color: ${({ theme }) => theme.Colors.Neutrals.Dark};
    outline: 2px solid ${({ theme }) => theme.Colors.Theme.Primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

const StCloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`

const StModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`
